import {
    AddressDTO,
    AuditInvoiceSummaryDTO,
    CentralRegulatorDTO,
    CompanyDTO,
    CompanyRegistrationPeriodDTO,
    DocumentDescriptionDTO,
    EmployeeCaseDTO,
    EmployeeTransactionDTO,
    InvoiceStatus,
    LicensePricingType,
    OfficeDTO,
    PlatformLicenseActivationDTO,
    PlatformLicenseDTO,
    PlatformLicensePricingDTO,
    ProductDTO,
    SubProductDTO,
    CompanyRole,
    NoteDTO
} from '@iota/iota-management-portal-v4-client';
import moment from 'moment';
import {
    CaseAttribute,
    ProductAttribute,
    RegistrationPeriodAttribute,
    TransactionAttribute
} from '../shared/constants-and-enums/attribute-types';
import {
    COMPANY_DOCUMENT_NAME_REGEX,
    CompanyDocumentLabel,
    DocumentTypeKey,
    DocumentTypeKeyToDocumentType,
    DocumentTypeLabels
} from '../shared/constants-and-enums/document-types';
import { deepCopy } from '../shared/constants-and-enums/default-objects';
import { ProductCategoryDescriptions } from '../shared/constants-and-enums/product-category';
import { ALL_REQUEST_DOCUMENT_STATUS, RequestDocumentStatusLabel } from '../shared/constants-and-enums/request-document-labels';
import { EmployeeRequestStatusLabels, submissionStatusBadgeClass } from '../shared/constants-and-enums/request-status';
import {
    GKV_SUBMISSION_ANSWER_LABELS,
    SUBMISSION_DOCUMENT_TYPE_LABELS,
    TOPIC_CATEGORY_LABELS
} from '../shared/constants-and-enums/submission-labels';
import {
    caseStatusToName,
    formatBytes,
    getAttributeFirstValue,
    getAttributeValueInDefaultLanguage,
    parseApiDate,
    requestDocumentStatusToBorderClass,
    resolveCompanyUrl,
    toIsoTimestamp,
    transactionStatusToName
} from '../shared/utils';
import {
    AuditInvoiceSummaryEntity,
    CentralRegulatorEntity,
    CompanyEntity,
    CompanyRegistrationPeriodEntity,
    DocumentDescriptionEntity,
    EmployeeCaseEntity,
    EmployeeRequestDocumentEntity,
    EmployeeRequestEntity,
    EmployeeTransactionEntity,
    PayrollReportSummaryEntity,
    PlatformLicenseActivationWithLicenseEntity,
    PlatformLicenseBusinessPartnerInfo,
    PlatformLicenseEntity,
    PlatformLicensePricingEntity,
    ProductEntity
} from './entity-models';
import {
    PlatformLicensePricingTypeLabels,
    PlatformLicenseVariablePriceByLabels
} from '../shared/constants-and-enums/platform-license-types';
import {
    AddressDTO as InhCoreAddressDTO,
    CentralRegulatorDTO as InhCoreCentralRegulatorDTO,
    CompanyDTO as INHCoreCompanyDTO,
    DocumentLabel,
    EmployeeRequestStatus,
    ManagementSubmissionDTO,
    ManagementSubscriptionDTO,
    PayrollReportSummaryDTO,
    PayrollReportSummaryStatus,
    NoteDTO as InhCoreNoteDTO,
    RequestCategoryLevel1,
    SubmissionDocumentDTO,
    SubmissionDocumentType
} from '@iota/inh-core-v1-management-client';

export function buildEmployeeTransactionEntities(...transactions: EmployeeTransactionDTO[]): EmployeeTransactionEntity[] {
    return (
        transactions?.map((t) => {
            if (!t) return {};

            const transactionEntity: EmployeeTransactionEntity = deepCopy(t);

            transactionEntity.amount = getAttributeFirstValue(transactionEntity, TransactionAttribute.AMOUNT) ?? 0;
            transactionEntity.goodwillPayment = getAttributeFirstValue(transactionEntity, TransactionAttribute.GOODWILL_PAYMENT);
            transactionEntity.description = getAttributeFirstValue(transactionEntity, TransactionAttribute.DESCRIPTION) ?? '';
            transactionEntity.currency = getAttributeFirstValue(transactionEntity, TransactionAttribute.CURRENCY) ?? '';
            transactionEntity.statusDescription = transactionStatusToName(transactionEntity.status);

            return transactionEntity;
        }) ?? []
    );
}

export function buildEmployeeCaseEntities(...employeeCases: EmployeeCaseDTO[]): EmployeeCaseEntity[] {
    return employeeCases.map((t) => {
        const caseEntity: EmployeeCaseEntity = deepCopy(t);

        caseEntity.amount = getAttributeFirstValue(caseEntity, CaseAttribute.AMOUNT) ?? 0;
        caseEntity.currency = getAttributeFirstValue(caseEntity, CaseAttribute.AMOUNT_CURRENCY) ?? 'EUR';
        caseEntity.gkvReimbursement = getAttributeFirstValue(caseEntity, CaseAttribute.GKV_REIMBURSEMENT) ?? 0;
        caseEntity.pendingTransactionSum = getAttributeFirstValue(caseEntity, CaseAttribute.PENDING_TRANSACTION_SUM) ?? 0;
        caseEntity.transactionSum = getAttributeFirstValue(caseEntity, CaseAttribute.TRANSACTION_SUM) ?? 0;
        caseEntity.statusDescription = caseStatusToName(caseEntity.status);
        caseEntity.diagnosis = getAttributeFirstValue(caseEntity, CaseAttribute.DIAGNOSIS);
        caseEntity.exportNote = getAttributeFirstValue(caseEntity, CaseAttribute.EXPORT_NOTE);
        caseEntity.claimNote = getAttributeFirstValue(caseEntity, CaseAttribute.CLAIM_NOTE);
        caseEntity.foyerInvoiceNumber = getAttributeFirstValue(caseEntity, CaseAttribute.FOYER_INVOICE_NUMBER);
        caseEntity.mailSent = getAttributeFirstValue(caseEntity, CaseAttribute.MAIL_SENT);

        caseEntity.invoiceNumber = getAttributeFirstValue(caseEntity, CaseAttribute.INVOICE_NUMBER);

        if (!caseEntity.employeeProductSubscriptionIds) {
            caseEntity.employeeProductSubscriptionIds = [];
        }
        if (!caseEntity.employeeSubProductSubscriptionIds) {
            caseEntity.employeeSubProductSubscriptionIds = [];
        }
        if (!caseEntity.notes) {
            caseEntity.notes = [];
        }
        if (!caseEntity.history) {
            caseEntity.history = [];
        }

        caseEntity.notes.sort((a, b) => moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime());
        caseEntity.history.sort((a, b) => moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime());

        return caseEntity;
    });
}

const CLOSED_SUBMISSION_STATUSES: EmployeeRequestStatus[] = [
    EmployeeRequestStatus.REJECTED,
    EmployeeRequestStatus.TRANSFERRED,
    EmployeeRequestStatus.COMPLETED
];

export function approvedDocuments(requests: EmployeeRequestEntity[]): EmployeeRequestDocumentEntity[] {
    return requests.flatMap((request) => request.employeeRequestDocuments.filter((document) => document.isApproved));
}

export function approvedDocumentsOfApprovedSubmissions(requests: EmployeeRequestEntity[]): EmployeeRequestDocumentEntity[] {
    return approvedDocuments(requests.filter((request) => request.status === EmployeeRequestStatus.APPROVED));
}

export function buildEmployeeRequestEntities(...submissions: ManagementSubmissionDTO[]): EmployeeRequestEntity[] {
    return submissions.map((submission) => {
        const categoryLevel1 =
            submission.categoryLevel1 === RequestCategoryLevel1.UNKNOWN ? undefined : (submission.categoryLevel1 ?? undefined);
        const categoryLabels = submission.categoryLabels ?? [];
        const preventionCourse = submission.preventionCourse;
        const documents = submission.documents ?? [];

        return {
            ...submission,
            categoryLevel1,
            categoryDescription: categoryLabels.join(' > '),
            isFullyCategorized: categoryLabels.length >= 2,
            statusDescription: EmployeeRequestStatusLabels[submission.status],
            statusBadgeClass: submissionStatusBadgeClass(submission.status, categoryLevel1 === RequestCategoryLevel1.PREVENTIONCOURSES),
            isClosed: CLOSED_SUBMISSION_STATUSES.includes(submission.status),
            preventionCourseSendToGkv: preventionCourse?.sendToGkv ?? true,
            preventionCourseTopicDescription: preventionCourse?.topic ? TOPIC_CATEGORY_LABELS[preventionCourse.topic] : undefined,
            preventionCourseGkvSubmissionDescription: preventionCourse?.gkvSubmission
                ? GKV_SUBMISSION_ANSWER_LABELS[preventionCourse.gkvSubmission]
                : undefined,
            hasInsuranceResponse: documents.some((document) => document.type === SubmissionDocumentType.INSURANCE_RESPONSE),
            hasAdditionalInformation: !!submission.note || !!submission.subjectArea || !!preventionCourse || !!submission.diopters,
            employeeRequestDocuments: buildEmployeeRequestDocumentEntities(...documents)
        };
    });
}

export function buildDocumentDescriptionEntities(
    documents: DocumentDescriptionDTO[],
    offices: OfficeDTO[] = []
): DocumentDescriptionEntity[] {
    return documents.map((doc) => {
        const documentEntity: DocumentDescriptionEntity = deepCopy(doc);

        documentEntity.fileType = documentEntity?.name.split('.').slice(-1).join('').toLowerCase();
        documentEntity.formattedBytes = formatBytes(documentEntity.size, 2);

        const regexMatch = doc?.name?.match(COMPANY_DOCUMENT_NAME_REGEX);
        if (!!regexMatch && regexMatch?.length >= 5) {
            documentEntity.businessPartnerNumber = regexMatch?.[1];
            const documentTypeString = regexMatch?.[2];
            documentEntity.fileDate = parseApiDate(regexMatch?.[3]);
            documentEntity.fileDateString = regexMatch?.[3];
            documentEntity.numberCount = parseInt(regexMatch?.[4]);

            documentEntity.documentType = DocumentTypeKeyToDocumentType[documentTypeString as DocumentTypeKey];
            documentEntity.documentTypeLabel = DocumentTypeLabels?.[documentEntity?.documentType];

            documentEntity.officeName = offices?.find(
                (office) => office.companyOfficeNumber === documentEntity.businessPartnerNumber
            )?.name;
        }

        return documentEntity;
    });
}

export function buildEmployeeRequestDocumentEntities(...documents: SubmissionDocumentDTO[]): EmployeeRequestDocumentEntity[] {
    return documents.map((document) => {
        const labels: string[] = document.labels ?? [];
        const statusLabel = labels.find((label) => ALL_REQUEST_DOCUMENT_STATUS.includes(label));

        const isApproved = labels.includes(DocumentLabel.APPROVED);
        const isDiscarded = labels.includes(DocumentLabel.DISCARDED);
        const isReplaced = labels.includes(DocumentLabel.RESUBMITTED);
        const isResubmissionRequested = labels.includes(DocumentLabel.REQUEST_RESUBMISSION);

        return {
            ...document,
            formattedBytes: formatBytes(document.size, 2),
            fileType: (document.name ?? '').split('.').slice(-1).join('').toLowerCase(),
            typeLabel: document.type ? SUBMISSION_DOCUMENT_TYPE_LABELS[document.type] : undefined,
            statusBorderClass: requestDocumentStatusToBorderClass(statusLabel as RequestDocumentStatusLabel),
            isApproved,
            isDiscarded,
            isReplaced,
            isResubmissionRequested,
            isResubmission: labels.includes(DocumentLabel.RESUBMISSION),
            isNew: !statusLabel,
            canBeReset: (isApproved || isDiscarded) && !isResubmissionRequested && !isReplaced
        };
    });
}

// Products without an explicit priority sort to the end.
export const DEFAULT_PRODUCT_SORTING_PRIORITY = 999999999;

export function compareSubscriptionsByOrder(a: ManagementSubscriptionDTO, b: ManagementSubscriptionDTO): number {
    const orderDifference =
        (a.productSortingPriority ?? DEFAULT_PRODUCT_SORTING_PRIORITY) - (b.productSortingPriority ?? DEFAULT_PRODUCT_SORTING_PRIORITY);
    if (orderDifference !== 0) {
        return orderDifference;
    }
    return (a.productName ?? '').localeCompare(b.productName ?? '');
}

export function buildProductEntities(products: ProductDTO[], language = 'de'): ProductEntity[] {
    if (!products || products.length == 0) {
        return [];
    }

    return products
        .filter((p) => !!p)
        .map((product) => {
            return {
                ...product,
                name: getProductName(product, language),
                categoryDescription: ProductCategoryDescriptions.get(product?.productCategory),
                subProducts: product?.subProducts?.map((subProduct) => ({
                    ...subProduct,
                    name: getProductName(subProduct, language),
                    categoryDescription: ProductCategoryDescriptions.get(subProduct?.subProductCategory)
                }))
            };
        });
}

export function getProductName(product: ProductDTO | SubProductDTO, language = 'de'): string {
    let nameValue = getAttributeValueInDefaultLanguage(product, ProductAttribute.NAME, language);

    if (!nameValue) {
        nameValue = getAttributeValueInDefaultLanguage(product, ProductAttribute.NAME, 'de');
    }

    if (!nameValue) {
        nameValue = getAttributeValueInDefaultLanguage(product, ProductAttribute.NAME, 'en');
    }

    if (!nameValue) {
        const anyName = product.attributes?.find((att) => att.attributeName === ProductAttribute.NAME)?.strValue;
        if (anyName && anyName.length > 0) {
            nameValue = anyName[0];
        }
    }

    return nameValue || '';
}

export function buildCompanyRegistrationPeriodEntities(
    language: string,
    ...registrationPeriods: CompanyRegistrationPeriodDTO[]
): CompanyRegistrationPeriodEntity[] {
    return registrationPeriods.map((registrationPeriod) => {
        const registrationPeriodEntity: CompanyRegistrationPeriodEntity = deepCopy(registrationPeriod);

        registrationPeriodEntity.name = getAttributeValueInDefaultLanguage(registrationPeriod, RegistrationPeriodAttribute.NAME, language);
        registrationPeriodEntity.internalName = getAttributeValueInDefaultLanguage(
            registrationPeriod,
            RegistrationPeriodAttribute.INTERNAL_NAME,
            language
        );

        return registrationPeriodEntity;
    });
}

export function buildPlatformLicenseEntities(
    platformLicenses: PlatformLicenseDTO[],
    pricingsThatHaveNotBeenInvoiced: string[],
    licenseActivationsByLicense: Record<string, PlatformLicenseActivationDTO[]>,
    companyNameMap: Map<string, string>,
    companyNumberMap: Map<string, string>,
    centralRegulatorNameMap: Map<string, string>,
    centralRegulatorNumberMap: Map<string, string>
): PlatformLicenseEntity[] {
    if (!platformLicenses) return [];

    const today = moment();

    return platformLicenses?.map((platformLicense) => {
        const pricings = buildPlatformLicensePricingEntities(platformLicense?.pricings, pricingsThatHaveNotBeenInvoiced);
        const _generalPricings = pricings?.filter((pricing) => !pricing?.platformLicenseActivationId) ?? [];
        const currentlyEffectivePricing = _generalPricings?.find(
            (pricing) =>
                (!pricing?.effectiveFrom || moment(pricing?.effectiveFrom).isSameOrBefore(today)) &&
                (!pricing?.effectiveTo || moment(pricing?.effectiveTo).isSameOrAfter(today))
        );

        const sortedGeneralPricings = _generalPricings.sort((a, b) => {
            // The currently active pricing is always first
            if (a === currentlyEffectivePricing) return -1;
            if (b === currentlyEffectivePricing) return 1;

            // Order the remaining pricings by their starting date
            if (!a?.effectiveFrom) return 1;
            if (!b?.effectiveFrom) return -1;

            return moment(a?.effectiveFrom).isBefore(moment(b?.effectiveFrom)) ? 1 : -1;
        });

        const entity: PlatformLicenseEntity = {
            ...platformLicense,
            pricings,
            generalPricings: sortedGeneralPricings,
            currentlyEffectivePricing,
            currentlyEffectivePricingPriceMonthly: currentlyEffectivePricing?.licensePriceMonthly,
            currentlyEffectivePricingPriceYearly: currentlyEffectivePricing?.licensePriceYearly,
            businessPartnersThatUseOrOverrideThisLicenseLoaded: !!licenseActivationsByLicense[platformLicense.platformLicenseId],
            businessPartnersThatUseOrOverrideThisLicense: getBusinessPartnersThatUseOrOverrideThisLicense(
                licenseActivationsByLicense[platformLicense.platformLicenseId],
                companyNameMap,
                companyNumberMap,
                centralRegulatorNameMap,
                centralRegulatorNumberMap
            )
        };

        entity.licensePricingTypeLabel = PlatformLicensePricingTypeLabels?.[entity?.licensePricingType] ?? entity?.licensePricingType;
        // The "price based on" is only shown if not a Volume License/"Fixed price license"
        if (entity?.licensePricingType !== LicensePricingType.VOLUME_LICENSE) {
            entity.licenseVariablePriceBasedOnLabel = PlatformLicenseVariablePriceByLabels?.[entity?.licenseVariablePriceBy];
        }

        return entity;
    });
}

function getBusinessPartnersThatUseOrOverrideThisLicense(
    licenseActivations: PlatformLicenseActivationDTO[],
    companyNameMap: Map<string, string>,
    companyNumberMap: Map<string, string>,
    centralRegulatorNameMap: Map<string, string>,
    centralRegulatorNumberMap: Map<string, string>
): PlatformLicenseBusinessPartnerInfo[] {
    return (
        licenseActivations?.map((licenseActivation) => {
            const businessPartnerInfo: PlatformLicenseBusinessPartnerInfo = {};

            const parentId = licenseActivation?.parentSkId;
            if (companyNameMap.has(parentId)) {
                businessPartnerInfo.companyId = parentId;
                businessPartnerInfo.businessPartnerName = companyNameMap.get(parentId) ?? parentId;
                businessPartnerInfo.businessPartnerNumber = companyNumberMap.get(parentId) ?? parentId;
            } else if (centralRegulatorNameMap.has(parentId)) {
                businessPartnerInfo.centralRegulatorId = parentId;
                businessPartnerInfo.businessPartnerName = centralRegulatorNameMap.get(parentId) ?? parentId;
                businessPartnerInfo.businessPartnerNumber = centralRegulatorNumberMap.get(parentId) ?? parentId;
            } else {
                businessPartnerInfo.businessPartnerName = parentId;
                businessPartnerInfo.businessPartnerNumber = parentId;
            }

            businessPartnerInfo.overridesThisLicense = !!licenseActivation?.customPlatformLicensePricing;

            return businessPartnerInfo;
        }) ?? []
    );
}

function buildPlatformLicensePricingEntities(
    platformLicensePricings: PlatformLicensePricingDTO[],
    pricingsThatHaveNotBeenInvoiced: string[]
): PlatformLicensePricingEntity[] {
    return (
        platformLicensePricings?.map((pricing) => ({
            ...pricing,
            canBeDeleted: pricingsThatHaveNotBeenInvoiced?.includes(pricing.platformLicensePricingId)
        })) ?? []
    );
}

export function buildCompanyEntities(companies?: INHCoreCompanyDTO[]): CompanyEntity[] {
    return companies?.map((company) => {
        const entity: CompanyEntity = {
            companyId: company.id,
            companyNumber: company.number,
            createdAt: company.createdAt,
            employeesCount: company.employeesCount,
            addresses: company.addresses,
            globalLocationNumber: company.globalLocationNumber,
            roles: company.roles as CompanyRole[],
            name: company.name,
            companyRegistrationNumber: company.registrationNumber,
            centralRegulatorId: company.centralRegulatorId,
            formattedName: company?.name,
            isB2c: company?.isB2c,
            isDeactivated: company?.isDeactivated,
            companyDeactivationDate: company?.companyDeactivationDate,
            foyerCaseEmail: company?.foyerCaseEmail
        };

        return {
            ...entity,
            companyDetailsUrl: resolveCompanyUrl({ ...entity })
        };
    });
}

export const mapPortalAddressToInhCoreAddress = (address: AddressDTO): InhCoreAddressDTO => {
    return {
        addressId: address.addressId,
        addressType: address.addressType,
        city: address.city,
        country: address.country,
        countryCode: address.countryCode,
        creationDateTime: toIsoTimestamp(address.creationDateTime),
        houseNumber: address.houseNumber,
        name1: address.name1,
        name2: address.name2,
        parentSkId: address.parentSkId,
        postOfficeBox: address.postOfficeBox,
        street: address.street,
        street2: address.street2,
        zipCode: address.zipCode
    };
};

export function buildPlatformLicenseActivationWithLicenseEntities(
    businessPartner: CompanyDTO | CentralRegulatorDTO,
    licenseActivationsThatHaveNotBeenInvoiced: string[],
    allPlatformLicenses: PlatformLicenseEntity[]
): PlatformLicenseActivationWithLicenseEntity[] {
    if (!businessPartner) return [];

    return businessPartner?.platformLicenseActivations?.map((licenseActivation) => {
        const platformLicense = allPlatformLicenses?.find((license) => license.platformLicenseId === licenseActivation.platformLicenseId);

        const hasCustomPricing = !!licenseActivation?.customPlatformLicensePricing;
        const entity: PlatformLicenseActivationWithLicenseEntity = {
            ...licenseActivation,

            platformLicense,
            description: platformLicense?.description,
            licensePricingTypeLabel: platformLicense?.licensePricingTypeLabel,
            licenseVariablePriceBasedOnLabel: platformLicense?.licenseVariablePriceBasedOnLabel,

            hasCustomPricing,
            activePricing: hasCustomPricing ? licenseActivation?.customPlatformLicensePricing : platformLicense?.currentlyEffectivePricing,
            canBeDeleted: licenseActivationsThatHaveNotBeenInvoiced?.includes(licenseActivation.platformLicenseActivationId)
        };

        entity.licensePriceMonthly = entity?.activePricing?.licensePriceMonthly;
        entity.licensePriceYearly = entity?.activePricing?.licensePriceYearly;

        return entity;
    });
}

export function buildAuditInvoiceSummaryEntities(
    auditInvoiceSummaries: AuditInvoiceSummaryDTO[],
    centralRegulators?: CentralRegulatorEntity[],
    companies?: CompanyEntity[]
): AuditInvoiceSummaryEntity[] {
    return auditInvoiceSummaries?.map((auditInvoiceSummary): AuditInvoiceSummaryEntity => {
        let businessPartnerName: string;
        let businessPartnerNumber: string;
        let businessPartnerDetailsUrl: string;

        if (!!auditInvoiceSummary.centralRegulatorId) {
            const centralRegulator = centralRegulators?.find((c) => c.centralRegulatorId === auditInvoiceSummary.centralRegulatorId);
            businessPartnerName = centralRegulator?.name;
            businessPartnerNumber = centralRegulator?.centralRegulatorNumber;
            businessPartnerDetailsUrl = centralRegulator?.centralRegulatorDetailsUrl;
        } else if (!!auditInvoiceSummary.companyId) {
            const company = companies?.find((c) => c.companyId === auditInvoiceSummary.companyId);
            businessPartnerName = company?.name;
            businessPartnerNumber = company?.companyNumber;
            businessPartnerDetailsUrl = company?.companyDetailsUrl;
        }

        return {
            ...auditInvoiceSummary,

            businessPartnerName,
            businessPartnerNumber,
            businessPartnerDetailsUrl,

            documentUnread: auditInvoiceSummary?.invoiceReviewAttachment?.labels?.includes(CompanyDocumentLabel.UNREAD),
            hasAtLeastOneNote: auditInvoiceSummary?.invoiceReviewNotes?.length > 0,
            canBeRejectedAndApproved: auditInvoiceSummary?.invoiceStatus === InvoiceStatus.TO_BE_REVIEWED,
            hasFailedStatus: auditInvoiceSummary?.invoiceStatus === InvoiceStatus.GENERATION_FAILED
        };
    });
}

export function buildCentralRegulatorEntities(centralRegulators?: InhCoreCentralRegulatorDTO[]): CentralRegulatorEntity[] {
    return centralRegulators?.map((centralRegulator): CentralRegulatorEntity => {
        return {
            ...centralRegulator,
            centralRegulatorId: centralRegulator.id,
            centralRegulatorDetailsUrl: '/administration/central-regulators/' + centralRegulator.id,
            centralRegulatorDeactivationDate: centralRegulator.scheduledOrExecutedCentralRegulatorDeactivation?.deactivationDate
        };
    });
}

export function buildPayrollReportSummaryEntities(
    payrollReportSummaries: PayrollReportSummaryDTO[],
    companies?: CompanyEntity[]
): PayrollReportSummaryEntity[] {
    return payrollReportSummaries?.map((payrollReportSummary): PayrollReportSummaryEntity => {
        const company = companies?.find((c) => c.companyId === payrollReportSummary.companyId);

        return {
            ...payrollReportSummary,
            noteEntries: payrollReportSummary?.notes?.map(
                (note: InhCoreNoteDTO): NoteDTO => ({
                    noteId: note?.id,
                    note: note?.note,
                    parentSkId: note?.parentId,
                    creationBy: note?.creationBy,
                    creationDateTime: parseApiDate(note?.creationDateTime)
                })
            ),

            companyDetailsUrl: company?.companyDetailsUrl,
            hasAtLeastOneNote: payrollReportSummary?.notes?.length > 0,
            canBeRejectedAndApproved: payrollReportSummary?.status === PayrollReportSummaryStatus.TO_BE_REVIEWED,
            hasFailedStatus: payrollReportSummary?.status === PayrollReportSummaryStatus.GENERATION_FAILED
        };
    });
}
